import { CsxInternalToolsDarkPreset } from "@csx/clear-ng/presets";
import { resolvePresetValue } from "../shared/utils/resolvePresetValue";

// New models
export interface SystemResponse {
  corridors: Corridor[];
  trains: Train[];
}

export interface Train {
  trainClassCode: string;
  trainId: string;
  trainTypeCode: string;
  crewHoursOfService: string;
  crewOnDutyTime: string;
  lorCrewHosInd: string;
  onLastCrewLeg: boolean;
  onLastTrainSheet: boolean;
  onLastWorkOrderLeg: boolean;
  performanceClassification: string;
  reportedLengthClassification: string;
  runningDistributedPower: boolean;
  scheduledDepartureTimeStamp: string;
  trainState: string;
  workingHPTTClassification: string;
  trainStopped: boolean;
  commodityDesc: string;
  crewStatus: string;
  crewStationMilepost: string;
  crewTime: string;
  time: string;
  heading: number;
  speed: number;
  locoId: string;
  lastActiveReportedStation: string;
  nextEtaTerminalTime: string;
  status: string;
  lastReportedLateIndicator: string;
  lastReportedPerformance: string;
  originMilepost: string;
  trainStoppedStarted: string;
  trainStoppedHeaderTime: string;
  latitude: number;
  longitude: number;
  lastReportedStationSequence: string;
  trainGroups: string[];
  activeMilepostInfo: string;
  originTransportationMilepost: string;
  leadLocomotiveId: string;
  tripOptimizerActive?: boolean;
}

export interface Terminal {
  code: string;
  trainAwaitingDeparture: TrainAwaitingDeparture[];
  uid: string;
}

export interface TrainAwaitingDeparture {
  classCode: string;
  originTransportationMilepost: string;
  train6: string;
  typeCode: string;
}

export interface Corridor {
  approaching: string;
  code: string;
  departing: string;
  gisSchematicLevel: string;
  name: string;
  trainOrders: TrainOrder[];
  type: string;
  uid: string;
}

export interface TrainOrder {
  classCode: string;
  position: number;
  train6: string;
  typeCode: string;
}

// Corridor Level
export interface CorridorLevel {
  approaching: string;
  code: string;
  departing: string;
  gisSchematicLevel: string;
  name: string;
  trainOrders: TrainOrder[];
  type: string;
  uid: string;
  trains: Train[];
  tablePosition: ICoordinates;
  arrowDirection: number;
  showCount: number;
  trainsZindex: number;
  showNearByTrains?: boolean;
  showEquipmentInfo?: boolean;
  showTrainInfo?: boolean;
  showGateInfo?: boolean;
}

// Terminal Level
export interface TerminalLevel {
  code: string;
  name: string;
  trainAwaitingDeparture: TrainAwaitingDeparture[];
  uid: string;
  schematicId: string;
  milepost:string;
  awaitingDepartureTrains: Train[];
  terminalPosition: ICoordinates;
  nearByTrainsDirection: number;
  trainOrders: TrainOrder[];
  nearByTrains: Train[];
  showNearByTrains: boolean;
  nearByTrainsTableZIndex: number;
  showEquipmentInfo: boolean;
  showTrainInfo: boolean;
  showGateInfo: boolean;
  awaitingDepartureTrainsZIndex: number;
  isUpdatedByFilter: boolean;
  popupClickPoint?: {
    x: number;
    y: number;
    placements?: Record<string, { left: number; top: number; placedAt: number }>;
  };
  popupBounds?: { left: number; right: number; top: number; bottom: number };
}

export interface ICoordinates {
  startX?: number;
  startY?: number;
  endX?: number;
  endY?: number;
  sortOrder?: number;
}

export enum TrainPerformance {
  TRAIN_PERFORMANCE_UNSPECIFIED = 0,
  TRAIN_PERFORMANCE_EARLY = 1,
  TRAIN_PERFORMANCE_AHEAD_OF_TIME = 2,
  TRAIN_PERFORMANCE_DELAYED = 3,
  TRAIN_PERFORMANCE_LATE = 4
}


export const TrainPerformanceClassificationColor = {
  PERFORMANCE_CLASSIFICATION_LATE: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_LATE_2: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_LATE_4: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_LATE_8: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_LATE_12: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_DELAYED: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.warnMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.successMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_EARLY: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.infoMessage.color'
  ),
  PERFORMANCE_CLASSIFICATION_UNSPECIFIED: '#FFF'
}

export enum TrainPerformanceClassificationSeverity {
  PERFORMANCE_CLASSIFICATION_LATE = 'danger',
  PERFORMANCE_CLASSIFICATION_LATE_2 = 'danger',
  PERFORMANCE_CLASSIFICATION_LATE_4 = 'danger',
  PERFORMANCE_CLASSIFICATION_LATE_8 = 'danger',
  PERFORMANCE_CLASSIFICATION_LATE_12 = 'danger',
  PERFORMANCE_CLASSIFICATION_DELAYED = 'warn',
  PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME = 'success',
  PERFORMANCE_CLASSIFICATION_EARLY = 'info',
  PERFORMANCE_CLASSIFICATION_UNSPECIFIED = 'secondary'
}

export enum TrainStoppageLabel {
  All_Trains = 'All Trains',
  Stopped_trains = 'Stopped Trains'
}

// Default filter save/restore preferences
export interface FiltersPreferences {
  performance?: string[][];
  lateSubFilters?: string[][];
  commodities?: string[][];
  trainGroups?: string;
  trainStoppage?: string[][];
  trainLengths?: string[][];
}

export interface UserPreferences {
  filters?: {
    semanticFilter?: FiltersPreferences;
  };
  [key: string]: unknown;
}

export enum TrainStoppageSeverity {
  All_Trains = 'default',
  Stopped_trains = 'danger'
}

export const TrainStoppageColor = {
  All_Trains: '#B3CDFB',
  Stopped_trains: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  )
};

export enum TrainLengthClassification {
  REPORTED_LENGTH_CLASSIFICATION_1 = 0,
  REPORTED_LENGTH_CLASSIFICATION_2 = 1,
  REPORTED_LENGTH_CLASSIFICATION_3 = 2,
  REPORTED_LENGTH_CLASSIFICATION_4 = 3,
  REPORTED_LENGTH_CLASSIFICATION_5 = 4,
  REPORTED_LENGTH_CLASSIFICATION_UNSPECIFIED = 0
}

export enum TrainLengthClassificationColor {
  REPORTED_LENGTH_CLASSIFICATION_1 = '#B3CDFB',
  REPORTED_LENGTH_CLASSIFICATION_2 = '#656769',
  REPORTED_LENGTH_CLASSIFICATION_3 = '#919393',
  REPORTED_LENGTH_CLASSIFICATION_4 = '#BDBEBF',
  REPORTED_LENGTH_CLASSIFICATION_5 = '#D3D4D4'
}

export enum TrainLengthClassificationStatus {
  REPORTED_LENGTH_CLASSIFICATION_1 = 'REPORTED_LENGTH_CLASSIFICATION_1',
  REPORTED_LENGTH_CLASSIFICATION_2 = 'REPORTED_LENGTH_CLASSIFICATION_2',
  REPORTED_LENGTH_CLASSIFICATION_3 = 'REPORTED_LENGTH_CLASSIFICATION_3',
  REPORTED_LENGTH_CLASSIFICATION_4 = 'REPORTED_LENGTH_CLASSIFICATION_4',
  REPORTED_LENGTH_CLASSIFICATION_5 = 'REPORTED_LENGTH_CLASSIFICATION_5',
  REPORTED_LENGTH_CLASSIFICATION_UNSPECIFIED = 'REPORTED_LENGTH_CLASSIFICATION_UNSPECIFIED'
}

export const TrainPerformanceColor = {
  Late: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.errorMessage.color'
  ),
  Delayed: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.warnMessage.color'
  ),
  AOT: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.successMessage.color'
  ),
  Early: resolvePresetValue(
    CsxInternalToolsDarkPreset,
    'semantic.colorScheme.light.severities.infoMessage.color'
  )
}

export enum TrainPerformanceClassificationText {
  PERFORMANCE_CLASSIFICATION_LATE = 'Late',
  PERFORMANCE_CLASSIFICATION_LATE_2 = '2 hours',
  PERFORMANCE_CLASSIFICATION_LATE_4 = '4 hours',
  PERFORMANCE_CLASSIFICATION_LATE_8 = '8 hours',
  PERFORMANCE_CLASSIFICATION_LATE_12 = '12 hours',
  PERFORMANCE_CLASSIFICATION_DELAYED = 'Delayed',
  PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME = 'AOT',
  PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME_LABEL = 'Ahead Of Time',
  PERFORMANCE_CLASSIFICATION_EARLY = 'Early',
  PERFORMANCE_CLASSIFICATION_UNSPECIFIED = '',
  PERFORMANCE_CLASSIFICATION_UNSPECIFIED_LABEL = 'Waiting Departure'
}

export enum CorridorType {
  CORRIDOR_TYPE_TERMINAL = 'CORRIDOR_TYPE_TERMINAL',
  CORRIDOR_TYPE_CORRIDOR = 'CORRIDOR_TYPE_CORRIDOR'
}

export enum TrainPerformanceTextColor {
  PERFORMANCE_CLASSIFICATION_LATE = '#FFFFFF',
  PERFORMANCE_CLASSIFICATION_DELAYED = '#000000',
  PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME = '#FFFFFF',
  PERFORMANCE_CLASSIFICATION_EARLY = '#FFFFFF',
  PERFORMANCE_CLASSIFICATION_UNSPECIFIED = '#FFFFFF',
  Late = '#FFFFFF',
  Delayed = '#000000',
  AOT = '#FFFFFF',
  Early = '#FFFFFF'
}

export enum PerformanceClassificationStatus {
  LATE = 'PERFORMANCE_CLASSIFICATION_LATE',
  LATE_2 = 'PERFORMANCE_CLASSIFICATION_LATE_2',
  LATE_4 = 'PERFORMANCE_CLASSIFICATION_LATE_4',
  LATE_8 = 'PERFORMANCE_CLASSIFICATION_LATE_8',
  LATE_12 = 'PERFORMANCE_CLASSIFICATION_LATE_12',
  DELAYED = 'PERFORMANCE_CLASSIFICATION_DELAYED',
  AOT = 'PERFORMANCE_CLASSIFICATION_AHEAD_OF_TIME',
  EARLY = 'PERFORMANCE_CLASSIFICATION_EARLY',
  UNSPECIFIED = 'PERFORMANCE_CLASSIFICATION_UNSPECIFIED'
}

export interface Train {
  trainClassCode: string;
  trainId: string;
  trainTypeCode: string;
  crewHoursOfService: string;
  crewOnDutyTime: string;
  lorCrewHosInd: string;
  onLastCrewLeg: boolean;
  onLastTrainSheet: boolean;
  onLastWorkOrderLeg: boolean;
  performanceClassification: string;
  reportedLengthClassification: string;
  runningDistributedPower: boolean;
  scheduledDepartureTimeStamp: string;
  trainState: string;
  workingHPTTClassification: string;
  trainStopped: boolean;
  commodityDesc: string;
  crewStatus: string;
  crewStationMilepost: string;
  crewTime: string;
  time: string;
  heading: number;
  speed: number;
  locoId: string;
  lastActiveReportedStation: string;
  nextEtaTerminalTime: string;
  status: string;
  lastReportedLateIndicator: string;
  lastReportedPerformance: string;
  originMilepost: string;
  trainStoppedStarted: string;
  trainStoppedHeaderTime: string;
  latitude: number;
  longitude: number;
  lastReportedStationSequence: string;
  trainGroups: string[];
  activeMilepostInfo: string;
  originTransportationMilepost: string;
  foreignLocoPower: boolean;
  tripOptimizer: boolean;
  inTerminal: boolean;
  tripOptimizerActive?: boolean;
  stoppedTimeInMinutes?:number
}
